import React, { useState, useEffect } from 'react';
import { formatIngredientParts } from '../utils/quantityFormat';
import { ingredientUnitsMatch } from '../utils/weightUnits';
import { useWeightUnit } from '../hooks/useWeightUnit';
import './IngredientVerificationModal.css';

const IngredientVerificationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  aggregatedIngredients, 
  sourceRecipes,
  isLoading 
}) => {
  const weightUnit = useWeightUnit();
  const [selectedIngredients, setSelectedIngredients] = useState(new Set());

  // Initialize with all ingredients selected by default
  useEffect(() => {
    if (aggregatedIngredients.length > 0) {
      setSelectedIngredients(new Set(aggregatedIngredients.map((_, index) => index)));
    }
  }, [aggregatedIngredients]);

  const handleIngredientToggle = (index) => {
    const newSelected = new Set(selectedIngredients);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedIngredients(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedIngredients(new Set(aggregatedIngredients.map((_, index) => index)));
  };

  const handleDeselectAll = () => {
    setSelectedIngredients(new Set());
  };

  const handleConfirm = () => {
    const filteredIngredients = aggregatedIngredients.filter((_, index) => 
      selectedIngredients.has(index)
    );
    onConfirm(filteredIngredients);
  };

  const getSourceRecipes = (ingredientName, ingredientUnit, ingredientSource) => {
    // For food items, find the matching food source
    if (ingredientSource === 'food') {
      return sourceRecipes.filter(recipe => 
        recipe.isFood && recipe.title?.toLowerCase() === ingredientName.toLowerCase()
      );
    }
    
    // For regular recipe ingredients
    return sourceRecipes.filter(recipe => 
      !recipe.isFood && recipe.ingredients?.some(ing => {
        if (typeof ing === 'string') {
          return ing.toLowerCase() === ingredientName.toLowerCase();
        }
        return ing.name?.toLowerCase() === ingredientName.toLowerCase() && 
          ingredientUnitsMatch(ing.unit, ingredientUnit);
      })
    );
  };

  if (!isOpen) return null;

  return (
    <div className="ingredient-verification-overlay">
      <div className="ingredient-verification-modal">
        <div className="modal-header">
          <h2>Review Your Shopping List</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-content">
          <div className="verification-info">
            <p>Review the ingredients that will be added to your Instacart shopping list. 
               You can deselect any ingredients you don't need.</p>
            <div className="note">
              <span className="note-icon">💡</span>
              <span>Once ingredients are sent to Instacart, you can modify them there too.</span>
            </div>
          </div>

          <div className="shopping-list-container">
            <div className="list-header">
              <h3>Shopping List ({selectedIngredients.size} of {aggregatedIngredients.length} selected)</h3>
              <div className="bulk-actions">
                <button className="select-all-btn" onClick={handleSelectAll}>
                  Select All
                </button>
                <button className="deselect-all-btn" onClick={handleDeselectAll}>
                  Deselect All
                </button>
              </div>
            </div>

            <div className="shopping-list-table">
              <div className="table-header">
                <div className="col-checkbox">Select</div>
                <div className="col-ingredient">Ingredient</div>
                <div className="col-quantity">Quantity</div>
                <div className="col-sources">From Recipes</div>
              </div>
              
              <div className="table-body">
                {aggregatedIngredients.map((ingredient, index) => {
                  const isSelected = selectedIngredients.has(index);
                  const matchingRecipes = getSourceRecipes(ingredient.name, ingredient.unit, ingredient.source);
                  const isFood = ingredient.source === 'food';
                  
                  return (
                    <div 
                      key={index} 
                      className={`table-row ${isSelected ? 'selected' : 'deselected'} ${isFood ? 'food-item' : ''}`}
                    >
                      <div className="col-checkbox">
                        <input 
                          type="checkbox" 
                          checked={isSelected}
                          onChange={() => handleIngredientToggle(index)}
                          className="ingredient-checkbox"
                        />
                      </div>
                      
                      <div className="col-ingredient">
                        <span className="ingredient-name">{ingredient.name}</span>
                        {ingredient.brandOwner && (
                          <span className="ingredient-brand">{ingredient.brandOwner}</span>
                        )}
                      </div>
                      
                      <div className="col-quantity">
                        <span className="ingredient-quantity">
                          {(() => {
                            const parts = formatIngredientParts(ingredient, 1, weightUnit);
                            return `${parts.amount} ${parts.unit}`.trim();
                          })()}
                        </span>
                      </div>
                      
                      <div className="col-sources">
                        <div className="source-recipes">
                          {isFood ? (
                            <div className="recipe-source food-source">
                              <span className="food-icon">🍎</span>
                              <span className="recipe-title">Individual Food</span>
                            </div>
                          ) : matchingRecipes.length > 0 ? (
                            matchingRecipes.map((recipe, recipeIndex) => (
                              <div key={recipeIndex} className="recipe-source">
                                <img 
                                  src={recipe.image} 
                                  alt={recipe.title}
                                  className="recipe-thumbnail"
                                  onError={(e) => {
                                    e.target.src = 'https://storage.googleapis.com/freshmenulab/freshmenulab_logo.png';
                                  }}
                                />
                                <span className="recipe-title">{recipe.title}</span>
                              </div>
                            ))
                          ) : (
                            <span className="no-source">-</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="confirm-button" 
            onClick={handleConfirm}
            disabled={selectedIngredients.size === 0 || isLoading}
          >
            {isLoading ? 'Adding to Instacart...' : `Add ${selectedIngredients.size} Items to Instacart`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IngredientVerificationModal;